import { EternolDB } from "../lib/db/index.js";
import { ArmyData } from "../lib/db/types.js";
import { Army } from "./army.js";
import { Ruleset, RulesetData } from "./ruleset.js";

type Semver = { major: number; minor: number; patch: number };

function parseSemver(version: string): Semver {
  const [major = 0, minor = 0, patch = 0] = version.split(".").map(Number);
  return { major, minor, patch };
}

function compareSemver(a: Semver, b: Semver) {
  return a.major - b.major || a.minor - b.minor || a.patch - b.patch;
}

/**
 * Whether `candidate` satisfies the caret range `^required`, i.e. it is the
 * same or a newer version without a breaking change. For 0.x versions, the
 * minor version is considered breaking, as per semver.
 */
function isCompatible(candidate: Semver, required: Semver) {
  if (compareSemver(candidate, required) < 0) {
    return false;
  }

  if (required.major === 0) {
    return candidate.major === 0 && candidate.minor === required.minor;
  }

  return candidate.major === required.major;
}

/**
 * Holds every ruleset bundled with the app and resolves the right one for an
 * army based on its `ruleset_version`.
 */
export class RulesetRegistry {
  /** Sorted from the latest to the oldest. */
  readonly all: Ruleset[];
  readonly latest: Ruleset;

  constructor(datas: RulesetData[], db?: EternolDB) {
    this.all = datas
      .map((data) => new Ruleset(data, db))
      .sort((a, b) =>
        compareSemver(parseSemver(b.version), parseSemver(a.version)),
      );

    const latest = this.all.at(0);
    if (!latest) {
      throw new Error("At least one ruleset must be provided.");
    }

    this.latest = latest;
  }

  /**
   * Resolves a version to a bundled ruleset: the newest ruleset that is
   * semver-compatible with the requested version (i.e. satisfies `^version`),
   * and the latest ruleset as the last resort.
   *
   * This means that minor and patch updates are applied automatically, and
   * only major updates require the army to be explicitly updated (see
   * {@link isOutdated}).
   */
  resolve(version: string) {
    const required = parseSemver(version);
    const compatible = this.all.find((r) =>
      isCompatible(parseSemver(r.version), required),
    );
    if (compatible) {
      return compatible;
    }

    console.warn(
      `No ruleset compatible with version ${version}, falling back to latest.`,
    );
    return this.latest;
  }

  /**
   * Whether a newer, breaking (major) version of the given ruleset exists.
   */
  isOutdated(ruleset: Ruleset) {
    return !isCompatible(
      parseSemver(this.latest.version),
      parseSemver(ruleset.version),
    );
  }

  createArmy(data: ArmyData) {
    return new Army(this.resolve(data.ruleset_version), data);
  }
}
